import { CheckOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import styled from '@emotion/styled';

import { Segmented } from '../../../components/Layout/PagePieces';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import { ButtonLink } from '../../../components/Link/Link';
import Page from '../../../components/Page/Page';
import { customerPath, useInbox } from '../../../context/InboxContext';
import { InboxItem, InboxKind, KIND_LABELS } from '../../../models/inbox/inbox.model';
import { RISK_CENTER } from '../../../routes/routeConstants';
import { timeAgo } from '../../../utils/utils';

type Filter = 'all' | InboxKind;

// Every decision waiting on a person, most urgent first (Linear/Stripe). Each
// row says what happened, why it matters, and has one button to deal with it.
// j / k move between rows, Enter opens one.
const Inbox = () => {
  const { items, inProgress, loaded, updatedAt, refresh } = useInbox();
  const [filter, setFilter] = useState<Filter>('all');
  const [, tick] = useState(0);
  const navigate = useNavigate();
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Keep "Updated 2m ago" honest between polls.
  useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(timer);
  }, []);

  const kinds = useMemo(() => Array.from(new Set(items.map((i) => i.kind))), [items]);
  const { now, later, ordered } = useMemo(() => {
    const shown = filter === 'all' ? items : items.filter((i) => i.kind === filter);
    const urgent = shown.filter((i) => i.severity === 'critical' || i.severity === 'high');
    const rest = shown.filter((i) => i.severity === 'medium' || i.severity === 'low');
    return { now: urgent, later: rest, ordered: [...urgent, ...rest] };
  }, [items, filter]);

  useEffect(() => {
    if (filter !== 'all' && !kinds.includes(filter)) setFilter('all');
  }, [filter, kinds]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || e.metaKey || e.ctrlKey) return;
      const index = rowRefs.current.findIndex((el) => el === document.activeElement);
      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault();
        const next = e.key === 'j' ? Math.min(index + 1, ordered.length - 1) : Math.max(index - 1, 0);
        rowRefs.current[next]?.focus();
      } else if (e.key === 'Enter' && index >= 0) {
        e.preventDefault();
        navigate(ordered[index].action.to);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ordered, navigate]);

  let rowIndex = -1;
  const renderRow = (item: InboxItem) => {
    rowIndex += 1;
    const i = rowIndex;
    return (
      <Row
        key={item.id}
        ref={(el) => {
          rowRefs.current[i] = el;
        }}
        tabIndex={0}
        className={item.severity}
        aria-label={`${item.severity}: ${item.title}`}
      >
        <span className='bar' aria-hidden />
        <div className='body'>
          <div className='top'>
            <span className='kind'>{KIND_LABELS[item.kind]}</span>
            {item.since && <span className='age'>{timeAgo(item.since)}</span>}
          </div>
          <div className='title'>{item.title}</div>
          <div className='why'>{item.why}</div>
          {item.meta.length > 0 && (
            <div className='meta'>
              {item.meta.map((m) => (
                <span key={m}>{m}</span>
              ))}
            </div>
          )}
        </div>
        <div className='actions'>
          {item.chatId && (
            <ButtonLink to={customerPath(item.chatId)} className='ghost' title='Open customer'>
              <UserOutlined /> <span>Customer</span>
            </ButtonLink>
          )}
          <ButtonLink to={item.action.to} className='primary'>
            {item.action.label}
          </ButtonLink>
        </div>
      </Row>
    );
  };

  return (
    <Page title='Hethera | Inbox'>
      <PageLayout>
        <PageHeader
          title='Inbox'
          className='no-bottom-padding'
          customComp={
            <Fresh>
              {updatedAt && <span>Updated {timeAgo(updatedAt.toISOString())}</span>}
              <button type='button' onClick={refresh} aria-label='Refresh now' title='Refresh now'>
                <ReloadOutlined />
              </button>
            </Fresh>
          }
        />

        {loaded && items.length === 0 ? (
          <Zero>
            <span className='check'>
              <CheckOutlined />
            </span>
            <h2>You’re all caught up</h2>
            <p>Nothing needs a decision right now. We check again every minute.</p>
            {inProgress > 0 && (
              <ButtonLink to={`/${RISK_CENTER}`} className='more'>
                {inProgress} alert{inProgress === 1 ? '' : 's'} you’re looking into →
              </ButtonLink>
            )}
          </Zero>
        ) : (
          <>
            <Toolbar>
              <Segmented role='tablist' aria-label='Show'>
                <button
                  type='button'
                  className={filter === 'all' ? 'active' : ''}
                  onClick={() => setFilter('all')}
                >
                  All <Count>{items.length}</Count>
                </button>
                {kinds.map((k) => (
                  <button
                    key={k}
                    type='button'
                    className={filter === k ? 'active' : ''}
                    onClick={() => setFilter(k)}
                  >
                    {KIND_LABELS[k]} <Count>{items.filter((i) => i.kind === k).length}</Count>
                  </button>
                ))}
              </Segmented>
              {inProgress > 0 && (
                <ButtonLink to={`/${RISK_CENTER}`} className='more'>
                  + {inProgress} you’re looking into
                </ButtonLink>
              )}
            </Toolbar>

            {now.length > 0 && (
              <Group>
                <h2>Act now</h2>
                <List>{now.map(renderRow)}</List>
              </Group>
            )}
            {later.length > 0 && (
              <Group>
                <h2>When you have a moment</h2>
                <List>{later.map(renderRow)}</List>
              </Group>
            )}
            {!loaded && <Loading>Checking everything…</Loading>}
          </>
        )}
      </PageLayout>
    </Page>
  );
};

export default Inbox;

const Fresh = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #7a8279;

  & button {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 1px solid var(--line);
    border-radius: 9px;
    background: #ffffff;
    color: var(--muted);
    cursor: pointer;
  }
  & button:hover {
    color: var(--ink);
    border-color: var(--line-2);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  & .more {
    font-size: 13px;
    color: var(--ok);
  }
`;

const Count = styled.span`
  margin-left: 4px;
  font-variant-numeric: tabular-nums;
  color: #7a8279;
`;

const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;

  & h2 {
    margin: 0;
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 600;
    color: var(--muted);
    letter-spacing: 0;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: #ffffff;
  box-shadow: var(--shadow-xs);
  overflow: hidden;
`;

const Row = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px 16px 22px;
  border-top: 1px solid #efece4;
  outline: none;
  transition: background-color 0.15s ease;

  &:first-of-type {
    border-top: 0;
  }
  &:hover,
  &:focus-visible {
    background: var(--panel);
  }
  &:focus-visible {
    box-shadow: inset 0 0 0 2px rgba(31, 122, 69, 0.45);
  }

  & .bar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
  }
  &.critical .bar {
    background: var(--red);
  }
  &.high .bar {
    background: #e8742c;
  }
  &.medium .bar {
    background: #d9a21b;
  }
  &.low .bar {
    background: #d8d3c6;
  }

  & .body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  & .top {
    display: flex;
    gap: 8px;
    font-size: 12px;
    color: #7a8279;
  }
  & .kind {
    font-weight: 600;
  }
  &.critical .kind {
    color: var(--red);
  }
  &.high .kind {
    color: #c2410c;
  }
  & .title {
    font-size: 14.5px;
    font-weight: 600;
    color: var(--ink);
  }
  & .why {
    font-size: 13.5px;
    color: var(--ink-2);
    line-height: 1.45;
  }
  & .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
  }
  & .meta span {
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--ground);
    font-size: 12px;
    color: var(--muted);
    font-variant-numeric: tabular-nums;
  }

  & .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: none;
  }
  & .actions a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 14px;
    border-radius: 9px;
    font-size: 13px;
    white-space: nowrap;
  }
  & .actions .primary {
    background: var(--ink);
    color: #ffffff;
  }
  & .actions .primary:hover {
    background: #26332b;
  }
  & .actions .ghost {
    color: var(--muted);
    border: 1px solid var(--line);
  }
  & .actions .ghost:hover {
    color: var(--ink);
    border-color: var(--line-2);
  }

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;

    & .actions {
      justify-content: flex-end;
    }
  }
`;

const Zero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 72px 24px;
  text-align: center;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background:
    radial-gradient(circle at 50% 0%, rgba(198, 241, 96, 0.25), transparent 60%),
    #ffffff;

  & .check {
    display: grid;
    place-items: center;
    width: 56px;
    height: 56px;
    margin-bottom: 8px;
    border-radius: 999px;
    background: var(--accent);
    color: var(--ink);
    font-size: 24px;
  }
  & h2 {
    margin: 0;
    font-size: 22px;
    color: var(--ink);
  }
  & p {
    margin: 0;
    font-size: 14px;
    color: var(--muted);
  }
  & .more {
    margin-top: 8px;
    font-size: 13px;
    color: var(--ok);
  }
`;

const Loading = styled.div`
  font-size: 13px;
  color: #7a8279;
`;
